import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Switch from '@mui/material/Switch';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import {
  paperStyles,
  feedbackCircleStyles,
  textStyles,
  textFieldStyles,
  radioButtonStyles,
} from './styles';
import { months } from './data';
import { useRiskFactors } from '../hooks';
import {
  isTemplateFieldDataUpdated,
  selectedRiskAssessmentFilters,
  maintainSelectedEquipments,
  selectedAssemblyIds,
  unselectedAssemblyIds,
  riskAssessmentAssemblyTemplate,
} from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { abyssColors } from '@/theme/colors';
import OpenDialogButton from '../shared/OpenDialogButton';
import useDialogState from '../hooks/useDialogState';
import * as riskState from '../state/atoms';
import * as analysisState from '@/components/Analysis/state';
import { editorStyles } from '../ProbabilityOfFailure/styles';
import { AbyssIcon } from '../../../../assets/icons/AbyssIcon';
import {
  GetMaterialTemplateByTypeQuery,
  useGetMaterialTemplateByTypeLazyQuery,
  useUpdateRiskAssessmentTemplateFieldDataAndTransitionPeriodMutation,
  GetAssembliesByRiskAssessmentFiltersDocument,
  GetVersionsDocument,
  MaterialType,
  TemplateTypeEnum,
  useBulkCreateCommentsForAssembliesMutation,
  RiskFieldsTypeEnum,
  AllCommentsForRiskAssessmentDocument,
} from '@/__generated__/graphql';
import { CalculateMenu, CalculateMenuRef } from '../shared/CalculateMenu';
import { BULK_COMMENTS_FEATURE_FLAG } from '@/constants';
import { ActionButton, RiskTextField } from '../shared/styles';
import { CONCRETE, REINFORCED_CONCRETE, STEEL } from '../constants';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

type TransitionPeriodProps = {
  updateAllAssemblies: boolean;
  selected: boolean;
};

export const TransitionPeriod = ({ updateAllAssemblies, selected }: TransitionPeriodProps) => {
  const [manualInputSwitch, setManualInputSwitch] = useState(false);
  const [calculatedTransitionPeriod, setCalculatedTransitionPeriod] = useState<number>(0);
  const [selectedInputs, setSelectedInputs] = useState<{ [key: string]: string | null }>({
    risk_coating_system: '',
    risk_corrosivity_category: '',
    risk_structure_age: '',
    risk_inspection_year: '',
    risk_cathodic_protection: 'false',
    risk_transition_period: '',
    risk_general_comments_transition_period: '',
    risk_thickness: '',
    risk_material_type: '',
    risk_environmental_exposure: '',
    risk_manual_transition: '',
  });
  const [materialType, setMaterialType] = useState<string>('');
  const [isTransitionPeriodCalculated, setIsTransitionPeriodCalculated] = useAtom(
    riskState.transitionCalculated
  );
  const [selectedFilters, setSelectedFilters] = useAtom(selectedRiskAssessmentFilters);
  const assemblyIds = useAtomValue(selectedAssemblyIds);
  const excludedAssemblyIds = useAtomValue(unselectedAssemblyIds);
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const suppressMissingCommentsCheck = useAtomValue(riskState.suppressMissingCommentsCheck);
  const setIsTemplateFieldDataUpdated = useSetAtom(isTemplateFieldDataUpdated);
  const setShowBulkCommentsDialog = useSetAtom(riskState.showBulkCommentsDialog);
  const [isBulkCommentsUpdateRequested, setIsBulkCommentsUpdateRequested] = useAtom(
    riskState.isBulkCommentsUpdateRequested
  );
  const setShowMissingCommentsDialog = useSetAtom(riskState.showMissingCommentsDialog);
  const [ignoreMissingComments, setIgnoreMissingComments] = useAtom(
    riskState.ignoreMissingComments
  );
  const structureInfo = useAtomValue(analysisState.structureFeatures);

  const calculateMenuRef = useRef<CalculateMenuRef>(null);

  const { resetDialogState } = useDialogState();

  const setMaintainSelections = useSetAtom(maintainSelectedEquipments);
  const setInvalidDamageClassCount = useSetAtom(riskState.invalidDamageClassCount);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const { templateId, structureId, calculatedValues } = useRiskFactors(selectedInputs);
  const [bulkCreateCommentsForAssemblies, { loading: loadingComments }] =
    useBulkCreateCommentsForAssembliesMutation();
  const bulkComments = useAtomValue(riskState.transitionCommentsState);
  const [materialTypeTemplates, setMaterialTypeTemplates] = useState<
    GetMaterialTemplateByTypeQuery['allTemplates'][0]['materialType']
  >([]);

  const [thicknessOptions, setThicknessOptions] = useState<string[]>([]);
  const [coatingSystemOptions, setCoatingSystemOptions] = useState<string[]>([]);
  const [corrosivityCategoryOptions, setCorrosivityCategoryOptions] = useState<string[]>([]);
  const [envrionmentalExposureOptions, setEnvrionmentalExposureOptions] = useState<string[]>([]);
  const [methodSelected, setMethodSelected] = useState<string>('');
  const [nextDate, setNextDate] = useState<Date>();
  const [futureDate, setFutureDate] = useState<Date>();
  const [hasPredefinedCalculation, setHasPredefinedCalculation] = useState(false);
  const setEditorExpanded = useSetAtom(riskState.editorExpanded);

  const riskFeatures: string[] = useMemo(() => {
    if (!structureInfo?.features?.riskAssessment) return [];
    return (
      (Array.isArray(structureInfo?.features?.riskAssessment) &&
        structureInfo?.features?.riskAssessment) ??
      []
    );
  }, [structureInfo?.features?.riskAssessment]);

  const [fetchMaterialTemplateQuery] = useGetMaterialTemplateByTypeLazyQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchMaterialTemplateQuery({
        variables: {
          structureId: structureId,
          type: TemplateTypeEnum.Structure,
        },
      });

      if (response.data?.allTemplates) {
        setMaterialTypeTemplates(response.data.allTemplates[0].materialType ?? []);
      } else {
        setMaterialTypeTemplates([]);
      }
    };

    fetchData();
  }, [fetchMaterialTemplateQuery, structureId]);

  const handleSteelType = (selectedMaterialType: MaterialType) => {
    const { corrosivityCategory, thickness } = selectedMaterialType;

    const corrosivityCategoriesValues = corrosivityCategory?.[0]?.values
      ? Object.keys(corrosivityCategory[0].values)
      : [];
    setCorrosivityCategoryOptions(corrosivityCategoriesValues);

    setThicknessOptions((thickness ?? []).filter((item): item is string => item !== null));

    setCoatingSystemOptions(corrosivityCategory?.map((corrosivty) => corrosivty.name) ?? []);
  };

  const handleReinforcedConcreteType = (selectedMaterialType: MaterialType) => {
    const { environmentalClassification, environmentalExposure } = selectedMaterialType;

    const thickness = environmentalClassification?.[0]?.values
      ? Object.keys(environmentalClassification[0].values)
      : [];
    setThicknessOptions((thickness ?? []).filter((item): item is string => item !== null));

    setEnvrionmentalExposureOptions(
      environmentalExposure?.map((envrionmental) => envrionmental.name) ?? []
    );
  };

  const getFieldsForMaterialType = useCallback(
    (type: string) => {
      setMaterialType(type ?? '');

      if (materialTypeTemplates) {
        const selectedMaterialType = materialTypeTemplates.find((materialTypeTemplate) =>
          materialTypeTemplate
            ? materialTypeTemplate.type.toLowerCase().includes(type.toLowerCase())
            : undefined
        );

        if (selectedMaterialType) {
          if (type.toLowerCase() === STEEL) {
            handleSteelType(selectedMaterialType);
          } else if (
            type.toLowerCase() === REINFORCED_CONCRETE ||
            type.toLowerCase() === CONCRETE
          ) {
            handleReinforcedConcreteType(selectedMaterialType);
          }

          setHasPredefinedCalculation(true);
          setManualInputSwitch(false);
        } else {
          setHasPredefinedCalculation(false);
          setManualInputSwitch(true);
        }
      }
    },
    [materialTypeTemplates]
  );

  const [updateAssembliesTransitionPeriod, { loading }] =
    useUpdateRiskAssessmentTemplateFieldDataAndTransitionPeriodMutation({
      variables: {
        input: {
          structureId,
          templateId,
          assemblyIds,
          excludedAssemblyIds,
          selectedInputs: calculatedValues,
          selectedFilters,
          updateAllAssemblies,
          lastModifiedFor: 'Transition period',
        },
      },
      refetchQueries: [GetAssembliesByRiskAssessmentFiltersDocument, GetVersionsDocument],
      awaitRefetchQueries: true,
      onCompleted: (response) => {
        if (response.updateRiskAssessmentTemplateFieldDataAndTransitionPeriod) {
          const futureDateTransitionPeriod =
            response.updateRiskAssessmentTemplateFieldDataAndTransitionPeriod
              .calculatedTransitionPeriod?.futureDate;
          const nextDateTransitionPeriod =
            response.updateRiskAssessmentTemplateFieldDataAndTransitionPeriod
              .calculatedTransitionPeriod?.nextDate;
          const transitionPeriodCalculated =
            response.updateRiskAssessmentTemplateFieldDataAndTransitionPeriod
              .calculatedTransitionPeriod?.transitionPeriod;

          if (futureDateTransitionPeriod && nextDateTransitionPeriod) {
            setNextDate(new Date(nextDateTransitionPeriod));
            setFutureDate(new Date(futureDateTransitionPeriod));
          }
          setCalculatedTransitionPeriod(
            Number.parseInt(String(transitionPeriodCalculated ?? '0'), 10)
          );
          setIsTransitionPeriodCalculated(true);
          setInvalidDamageClassCount(
            response.updateRiskAssessmentTemplateFieldDataAndTransitionPeriod
              .invalidDamageClassCount ?? 0
          );
          showSnackBar('The transition period table has been updated successfully', 'success');
          setIsTemplateFieldDataUpdated(true);
          setEditorExpanded(false);
        }
      },
    });

  const materialTypeOptions = useMemo(() => {
    return template?.fields.find((field) => field.key === 'risk_material_type')?.options ?? [];
  }, [template?.fields]);

  const isGreaterThanCurrentYear = useMemo(() => {
    return selectedInputs.risk_structure_age
      ? Number.parseInt(selectedInputs.risk_structure_age, 10) > new Date().getFullYear()
      : Number.parseInt(selectedInputs.risk_inspection_year ?? '', 10) > new Date().getFullYear();
  }, [selectedInputs.risk_inspection_year, selectedInputs.risk_structure_age]);

  const isYearError = useMemo(() => {
    const yearPattern = /^\d{4}$/;

    if (selectedInputs.risk_structure_age === '' || selectedInputs.risk_inspection_year === '') {
      return false;
    }

    if (isGreaterThanCurrentYear) {
      return true;
    }

    if (selectedInputs.risk_structure_age !== '') {
      return !yearPattern.test(selectedInputs.risk_structure_age ?? '');
    }

    return !yearPattern.test(selectedInputs.risk_inspection_year ?? '');
  }, [
    isGreaterThanCurrentYear,
    selectedInputs.risk_inspection_year,
    selectedInputs.risk_structure_age,
  ]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, value: string, key: string) => {
      setIsTransitionPeriodCalculated(false);
      setSelectedInputs((previousInputs) => ({
        ...previousInputs,
        [key]: value || '',
      }));
    },
    []
  );

  const handleManualInputSwitch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setSelectedInputs((previousInputs) => ({
        ...previousInputs,
        ...(isChecked ? {} : { risk_transition_period: '' }),
        risk_manual_transition: isChecked ? 'Yes' : 'No',
      }));
      setManualInputSwitch(event.target.checked);
    },
    [setManualInputSwitch]
  );

  const handleClick = useCallback(() => {
    const commentsExist =
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '');
    if (!suppressMissingCommentsCheck && !commentsExist && !ignoreMissingComments) {
      setShowMissingCommentsDialog(true);
      return;
    }
    calculateMenuRef.current?.handleClose();
    setMaintainSelections(true);
    setIsTransitionPeriodCalculated(false);
    updateAssembliesTransitionPeriod();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
        refetchQueries: [AllCommentsForRiskAssessmentDocument],
      });
    resetDialogState();
    setIgnoreMissingComments(false);
  }, [
    bulkComments?.comments,
    suppressMissingCommentsCheck,
    ignoreMissingComments,
    setMaintainSelections,
    setIsTransitionPeriodCalculated,
    updateAssembliesTransitionPeriod,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    setIgnoreMissingComments,
    setShowMissingCommentsDialog,
  ]);

  const disableBulkComments = useMemo(() => {
    return !(
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '')
    );
  }, [bulkComments?.comments]);

  const handleBulkComments = useCallback(() => {
    if (
      !isBulkCommentsUpdateRequested &&
      calculatedValues.length > 0 &&
      calculatedValues.some((calculated) => calculated.value !== '')
    ) {
      setShowBulkCommentsDialog(true);
      return;
    }
    setMaintainSelections(true);
    calculateMenuRef.current?.handleClose();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
        refetchQueries: [AllCommentsForRiskAssessmentDocument],
        onCompleted: () => {
          showSnackBar('The comments have been updated successfully!', 'success');
          setSelectedInputs({});
          setIsTemplateFieldDataUpdated(true);
          setEditorExpanded(false);
        },
      });
    setIsBulkCommentsUpdateRequested(false);
    resetDialogState();
  }, [
    calculatedValues,
    setMaintainSelections,
    bulkComments?.comments,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    isBulkCommentsUpdateRequested,
    setShowBulkCommentsDialog,
    showSnackBar,
    setIsTemplateFieldDataUpdated,
    setEditorExpanded,
    setIsBulkCommentsUpdateRequested,
  ]);

  useEffect(() => {
    if (ignoreMissingComments && selected) handleClick();
  }, [handleClick, ignoreMissingComments, selected]);

  useEffect(() => {
    if (isBulkCommentsUpdateRequested && selected) handleBulkComments();
  }, [handleBulkComments, isBulkCommentsUpdateRequested, selected]);

  const handleMaterialTypeChange = useCallback(
    (value: string) => {
      if (value) {
        getFieldsForMaterialType(value);
        setSelectedInputs({
          risk_coating_system: '',
          risk_corrosivity_category: '',
          risk_structure_age: '',
          risk_inspection_year: '',
          risk_cathodic_protection: 'false',
          risk_transition_period: '',
          risk_general_comments_transition_period: '',
          risk_thickness: '',
          risk_material_type: value,
          risk_environmental_exposure: '',
        });
        setIsTransitionPeriodCalculated(false);
        setSelectedFilters([
          ...selectedFilters.filter((selectedFilter) => selectedFilter.id !== 'risk_material_type'),
          { id: 'risk_material_type', value },
        ]);
      } else {
        setMaterialType('');
        setSelectedFilters(
          selectedFilters.filter((selectedFilter) => selectedFilter.id !== 'risk_material_type')
        );
      }
    },
    [getFieldsForMaterialType, selectedFilters, setIsTransitionPeriodCalculated, setSelectedFilters]
  );

  const yearErrorMessage = useMemo(() => {
    const methodSelectedLabel = methodSelected === 'Method A' ? 'Build Year' : 'Inspection Year';
    if (isYearError) {
      return isGreaterThanCurrentYear
        ? `${methodSelectedLabel} cannot be in the future. Please enter a valid year`
        : 'Please enter a valid year';
    }
    return '';
  }, [isGreaterThanCurrentYear, isYearError, methodSelected]);

  return (
    <Paper
      data-testid="transition-period-component"
      elevation={0}
      sx={{ ...paperStyles, display: selected ? 'block' : 'none' }}
    >
      <Stack sx={editorStyles}>
        <Grid spacing={3} container>
          <Grid size={6}>
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <Autocomplete
                fullWidth
                sx={textFieldStyles}
                data-testid="material-type"
                options={materialTypeOptions}
                getOptionLabel={(option) => option}
                defaultValue=""
                value={materialType}
                autoHighlight
                includeInputInList
                renderInput={(parameters) => (
                  <RiskTextField {...parameters} label="Material Type" variant="standard" />
                )}
                onChange={(_, value) => handleMaterialTypeChange(value ?? '')}
              />
              <OpenDialogButton
                location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                identifer="risk_material_type"
                title="Material Type"
              />
            </Stack>
          </Grid>

          {materialType && (
            <>
              <Grid size={6}>
                <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                  <Autocomplete
                    sx={textFieldStyles}
                    data-testid="method"
                    options={['Method A', 'Method B']}
                    value={methodSelected}
                    defaultValue=""
                    disabled={!materialType}
                    autoHighlight
                    includeInputInList
                    renderInput={(parameters) => (
                      <RiskTextField {...parameters} label="Select Method" variant="standard" />
                    )}
                    onChange={(_event, value) => {
                      setMethodSelected(value ?? '');
                      setSelectedInputs((previousSate) => ({
                        ...previousSate,
                        risk_structure_age: '',
                        risk_inspection_year: '',
                      }));
                    }}
                  />
                  <OpenDialogButton
                    location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                    identifer="risk_selected_method"
                    title="Selected Method"
                  />
                </Stack>
              </Grid>

              {methodSelected === 'Method A' && (
                <Grid size={6}>
                  <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                    <RiskTextField
                      data-testid="built-year"
                      sx={textFieldStyles}
                      label="Built Year"
                      placeholder={`e.g. ${new Date().getFullYear()}`}
                      variant="standard"
                      value={selectedInputs?.risk_structure_age}
                      error={isYearError}
                      helperText={yearErrorMessage}
                      onChange={(event) =>
                        handleInputChange(event, event.target.value, 'risk_structure_age')
                      }
                      disabled={!materialType}
                    />
                    <OpenDialogButton
                      location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                      identifer="risk_structure_age"
                      title="Built Year"
                    />
                  </Stack>
                </Grid>
              )}

              {methodSelected === 'Method B' && (
                <Grid size={6}>
                  <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                    <RiskTextField
                      data-testid="inspection-year"
                      sx={textFieldStyles}
                      label="Inspection Year"
                      placeholder={`e.g. ${new Date().getFullYear()}`}
                      variant="standard"
                      value={selectedInputs?.risk_inspection_year}
                      error={isYearError}
                      helperText={yearErrorMessage}
                      onChange={(event) =>
                        handleInputChange(event, event.target.value, 'risk_inspection_year')
                      }
                      disabled={!materialType}
                    />
                    <OpenDialogButton
                      location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                      identifer="risk_inspection_year"
                      title="Inspection Year"
                    />
                  </Stack>
                </Grid>
              )}
            </>
          )}

          {hasPredefinedCalculation && (
            <>
              {materialType.toLowerCase() === STEEL && (
                <>
                  <Grid size={6}>
                    <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                      <Autocomplete
                        sx={textFieldStyles}
                        data-testid="coating-system"
                        options={coatingSystemOptions || []}
                        getOptionLabel={(option) => option}
                        defaultValue=""
                        value={selectedInputs?.risk_coating_system}
                        disabled={manualInputSwitch || !materialType}
                        autoHighlight
                        includeInputInList
                        renderInput={(parameters) => (
                          <RiskTextField
                            {...parameters}
                            label="Coating System"
                            variant="standard"
                          />
                        )}
                        onChange={(_event, value) =>
                          handleInputChange(_event, value as string, 'risk_coating_system')
                        }
                      />
                      <OpenDialogButton
                        location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                        identifer="risk_coating_system"
                        title="Coating System"
                      />
                    </Stack>
                  </Grid>

                  <Grid size={6}>
                    <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                      <Autocomplete
                        sx={textFieldStyles}
                        data-testid="corrosivity-category"
                        options={corrosivityCategoryOptions || []}
                        defaultValue=""
                        value={selectedInputs?.risk_corrosivity_category}
                        disabled={manualInputSwitch || !materialType}
                        autoHighlight
                        includeInputInList
                        renderInput={(parameters) => (
                          <RiskTextField
                            {...parameters}
                            label="Corrosivity Category"
                            variant="standard"
                          />
                        )}
                        onChange={(_event, value) =>
                          handleInputChange(_event, value as string, 'risk_corrosivity_category')
                        }
                      />
                      <OpenDialogButton
                        location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                        identifer="risk_corrosivity_category"
                        title="Corrosivity Category"
                      />
                    </Stack>
                  </Grid>
                </>
              )}

              {materialType.toLowerCase() === REINFORCED_CONCRETE ||
                (materialType.toLowerCase() === CONCRETE && (
                  <Grid size={6}>
                    <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                      <Autocomplete
                        sx={textFieldStyles}
                        data-testid="envrionmental-exposure"
                        options={envrionmentalExposureOptions || []}
                        defaultValue=""
                        value={selectedInputs?.risk_environmental_exposure}
                        disabled={manualInputSwitch || !materialType}
                        autoHighlight
                        includeInputInList
                        renderInput={(parameters) => (
                          <RiskTextField
                            {...parameters}
                            label="Envrionmental Exposure"
                            variant="standard"
                          />
                        )}
                        onChange={(_event, value) =>
                          handleInputChange(_event, value as string, 'risk_environmental_exposure')
                        }
                      />
                      <OpenDialogButton
                        location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                        identifer="risk_environmental_exposure"
                        title="Envrionmental Exposure"
                      />
                    </Stack>
                  </Grid>
                ))}

              <Grid size={6}>
                <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                  <Autocomplete
                    sx={textFieldStyles}
                    data-testid="thickness"
                    options={thicknessOptions || []}
                    defaultValue=""
                    value={selectedInputs?.risk_thickness}
                    disabled={manualInputSwitch || !materialType}
                    autoHighlight
                    includeInputInList
                    renderInput={(parameters) => (
                      <RiskTextField {...parameters} label="Thickness (mm)" variant="standard" />
                    )}
                    onChange={(_event, value) =>
                      handleInputChange(_event, value as string, 'risk_thickness')
                    }
                  />
                  <OpenDialogButton
                    location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                    identifer="risk_thickness"
                    title="Thickness"
                  />
                </Stack>
              </Grid>

              {materialType && materialType.toLowerCase() === STEEL && (
                <Grid size={6}>
                  <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
                    <FormControl data-testid="cathodic-protection-reading" sx={radioButtonStyles}>
                      <FormLabel>{`Cathodic protection reading (>850mv)`}</FormLabel>
                      <RadioGroup
                        row
                        value={selectedInputs?.risk_cathodic_protection}
                        onChange={(_event, value) =>
                          handleInputChange(_event, value, 'risk_cathodic_protection')
                        }
                      >
                        <FormControlLabel
                          value
                          control={<Radio />}
                          label="Yes"
                          disabled={manualInputSwitch || !materialType}
                        />
                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No"
                          disabled={manualInputSwitch || !materialType}
                        />
                      </RadioGroup>
                    </FormControl>
                    <OpenDialogButton
                      location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                      identifer="risk_cathodic_protection"
                      title="Cathodic Protection"
                    />
                  </Stack>
                </Grid>
              )}

              <Grid size={12} mt={3}>
                <Divider />
              </Grid>
            </>
          )}

          <Grid size={6}>
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <RiskTextField
                data-testid="transition-period"
                sx={textFieldStyles}
                label="Transition Period (years)"
                variant="standard"
                value={selectedInputs?.risk_transition_period}
                onChange={(event) =>
                  setSelectedInputs((previousInputs) => ({
                    ...previousInputs,
                    risk_transition_period: event.target.value,
                  }))
                }
                disabled={!materialType || !manualInputSwitch}
              />
              {manualInputSwitch && (
                <OpenDialogButton
                  location={RiskFieldsTypeEnum.TransitionPeriod.toString()}
                  identifer="risk_transition_period"
                  title="Transition Period"
                />
              )}
            </Stack>
          </Grid>

          <Grid size={6}>
            <Stack
              direction="row"
              alignItems="flex-end"
              width="100%"
              justifyContent="flex-end"
              sx={{
                '& .MuiFormControlLabel-root': {
                  marginLeft: 0,
                  marginRight: 0,
                },
              }}
            >
              {hasPredefinedCalculation && (
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch checked={manualInputSwitch} onChange={handleManualInputSwitch} />
                  }
                  label="Manual Transition Calculation"
                />
              )}
            </Stack>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ pl: 3, mt: 3, pt: 1 }}>
          Result
        </Typography>
        {loading && (
          <Stack alignItems="center" justifyContent="center" paddingY={2}>
            <img src="/assets/abyss_fabric_mini_logo_black.svg" alt="fabric-logo" />
            <Typography>Please wait</Typography>
          </Stack>
        )}

        {(!isTransitionPeriodCalculated || !calculatedTransitionPeriod) && !loading && (
          <Box sx={feedbackCircleStyles}>
            <FeedbackCircle
              backgroundColor="#f8f9fb"
              diameter={120}
              text={'No result'}
              icon={<AbyssIcon style={{ width: '2.2rem' }} iconName="table" />}
            />
          </Box>
        )}
        {isTransitionPeriodCalculated && !Number.isNaN(calculatedTransitionPeriod) && !loading && (
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography sx={{ pl: 1, pt: 1, font: 'Roboto', fontWeight: '400' }}>
                Period is set to every {calculatedTransitionPeriod} years
              </Typography>
            </Grid>

            <Grid size={3}>
              <Container
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AccessTimeFilledIcon sx={{ mr: 1 }} />
                <Stack sx={{ flexDirection: 'column' }}>
                  <Typography sx={textStyles}>Next</Typography>
                  <Typography sx={{ font: 'Roboto', color: '#C62828', fontWeight: '700' }}>
                    {nextDate ? `${months[nextDate.getMonth()]} ${nextDate.getFullYear()}` : 'N/A'}
                  </Typography>
                </Stack>
              </Container>
            </Grid>

            <Grid size={3}>
              <Container
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AccessTimeFilledIcon sx={{ mr: 1 }} />
                <Stack sx={{ flexDirection: 'column' }}>
                  <Typography sx={textStyles}>Future</Typography>
                  <Typography
                    sx={{ font: 'Roboto', color: abyssColors.primary[700], fontWeight: '700' }}
                  >
                    {futureDate
                      ? (() => {
                          if (futureDate.getFullYear() < new Date().getFullYear()) {
                            return 'N/A';
                          }
                          return `${months[futureDate.getMonth()]} ${futureDate.getFullYear()}`;
                        })()
                      : 'N/A'}
                  </Typography>
                  <Typography sx={textStyles}>If Applicable</Typography>
                </Stack>
              </Container>
            </Grid>
          </Grid>
        )}
      </Stack>
      <Stack alignItems="flex-end" mt={2}>
        {riskFeatures.includes(BULK_COMMENTS_FEATURE_FLAG) ? (
          <CalculateMenu
            ref={calculateMenuRef}
            onCalculate={handleClick}
            onUpdateComments={handleBulkComments}
            disableComments={disableBulkComments}
            disabled={isYearError || loading}
            loading={loading}
            loadingComments={loadingComments}
          />
        ) : (
          <ActionButton contained onClick={handleClick} disabled={isYearError || loading}>
            <>
              {loading ? 'Calculating...' : 'Calculate & Update Table'}
              {loading && <CircularProgress size={20} />}
            </>
          </ActionButton>
        )}
      </Stack>
    </Paper>
  );
};
